/**
 * "Time reading" — the clock time the school says a task's temperatures were
 * taken (PROD-1279 D9).
 *
 * Both school tasks carry ONE `<input type="time">` beside their signature
 * field. Transport folds it into the receipt's `arrivedAt`; Service sends it
 * as `readingAt` on `/service-temps`, where it becomes the printed record's
 * "Logged" time (until now a signed record printed a blank cell for every
 * service-line reading, because SERVICE rows carry no source event).
 *
 * A time is meaningless without its day, so the wizards combine it with the
 * task's own date. That combination is LOCAL — `new Date('2026-09-17T08:15:00')`
 * with no `Z`, the same rule `serviceDateToDate` uses in `format.ts`: the
 * school reads a wall clock, and a UTC parse would file a morning receipt as
 * the previous evening west of Greenwich.
 *
 * Pure so the wizards never re-derive any of it inline.
 */

import { todayLocalIso } from '@/lib/cafeteria/format'

/** A 24-hour `HH:MM` exactly as `<input type="time">` reports it. */
const READING_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

export function isReadingTime(value: string): boolean {
  return READING_TIME_PATTERN.test(value)
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * The input's starting value for a task on `isoDate`: the current clock time
 * when that IS today, otherwise blank. A past truck or a catch-up record must
 * never open pre-filled with "now" — the reading was taken on the day it was
 * taken, and a silently-stamped wrong time is worse than an empty box.
 */
export function defaultReadingTime(isoDate: string, now: Date = new Date()): string {
  if (todayLocalIso(now) !== isoDate) {
    return ''
  }
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`
}

/**
 * The inverse of `combineReadingInstant`: a stored instant back to the `HH:MM`
 * the input shows. LOCAL getters, deliberately — the wizard combined a wall
 * clock with the service day, so reading it back in UTC would show a signed
 * 11:20 record as 18:20 (or 04:20 the day before) on reopen.
 *
 * Null for a missing or unparseable value; the caller then falls back to its
 * own default rather than showing a time nobody recorded.
 */
export function readingTimeFromInstant(iso: string | null | undefined): string | null {
  if (!iso) {
    return null
  }
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) {
    return null
  }
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`
}

/**
 * `isoDate` + `HH:MM` → an ISO instant, or null when the time is blank or
 * malformed (the field is optional; callers fall back to their own default).
 */
export function combineReadingInstant(isoDate: string, time: string): string | null {
  if (!isReadingTime(time)) {
    return null
  }
  const combined = new Date(`${isoDate}T${time}:00`)
  return Number.isNaN(combined.getTime()) ? null : combined.toISOString()
}
